<template>
  <div class="login-container">
    <div class="login-form-wrapper">
      <div class="login-header">
        <img src="../assets/images/logo.svg" alt="谐伴智能" class="logo">
        <h1>谐伴智能</h1>
        <p>后台管理系统</p>
      </div>
      <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef" class="login-form">
        <el-form-item prop="username">
          <el-input v-model="loginForm.username" placeholder="请输入用户名">
            <template #prefix>
              <el-icon><UserFilled /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="请输入密码"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
            <template #suffix>
              <el-icon @click="showPassword = !showPassword" style="cursor: pointer;">
                <Hide v-if="showPassword" />
                <View v-else />
              </el-icon>
            </template>
          </el-input>
        </el-form-item>
        <div class="login-options">
          <el-checkbox v-model="loginForm.remember">记住我</el-checkbox>
          <el-link type="primary" :underline="false" class="forgot-password" @click="handleForgotPassword">忘记密码？</el-link>
        </div>
        <el-form-item>
          <el-button type="primary" class="login-btn" @click="handleLogin" :loading="loading">
            登录
          </el-button>
        </el-form-item>

      </el-form>
    </div>

    <!-- 图片验证组件 -->
    <ImageVerification
      v-model="showVerification"
      mode="random"
      @success="onVerificationSuccess"
      @fail="onVerificationFail"
    />
    
    <!-- 备案信息 -->
    <div class="beian-info">
      <p><a href="https://beian.miit.gov.cn/" target="_blank">蜀ICP备2026026507号</a></p>
      <p>&copy; 谐伴智能版权所有 | <a href="https://xiebankeji.com" target="_blank">https://xiebankeji.com</a></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { UserFilled, Lock, View, Hide } from '@element-plus/icons-vue'
import ImageVerification from '../components/ImageVerification.vue'
import { apiService } from '../services/api-service'

const router = useRouter()
const loginFormRef = ref()
const loading = ref(false)
const showVerification = ref(false)
const showPassword = ref(false)

const loginForm = reactive({
  username: '',
  password: '',
  remember: false
})

const loginRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ]
}

let pendingLoginCallback: (() => void) | null = null
let loginUserData: any = null

const handleLogin = async () => {
  if (!loginFormRef.value) return
  
  try {
    await loginFormRef.value.validate()
    loading.value = true
    
    // V1.3.87：后台登录走专用管理员入口，仅超级管理员(super_admin)与被授权管理员(admin)可进入
    const response = await apiService.post('/api/auth/admin/login', {
      phone: loginForm.username,
      password: loginForm.password
    })

    if (response.success) {
      const data = response.data
      // 前端二次校验真实身份：其他任何身份（普通用户/服务员/客服）均无后台受理权限
      if (data.mode !== 'admin' && data.mode !== 'super_admin') {
        ElMessage.error('该账号无后台管理权限，仅限超级管理员授权的账号登录')
        loading.value = false
        return
      }
      if (data.mode === 'admin' && data.adminStatus === 'inactive') {
        ElMessage.error('该管理员账号已被停用，请联系超级管理员')
        loading.value = false
        return
      }
      loginUserData = data

      pendingLoginCallback = () => {
        completeLogin(data)
      }

      showVerification.value = true
    } else {
      ElMessage.error(response.message || '登录失败')
    }
    
    loading.value = false
    
  } catch (error: any) {
    console.error('登录失败:', error)
    const errorMsg = error.response?.data?.message || error.message || '登录失败，请稍后重试'
    ElMessage.error(errorMsg)
    loading.value = false
  }
}

const completeLogin = (data: any) => {
  const userInfo = {
    id: data.userId,
    username: data.nickname || data.phone,
    email: data.email || '',
    phone: data.phone,
    // V1.3.87：角色直接采用后端真实 mode（super_admin=唯一超级管理员，admin=被授权分项管理员）
    role: data.mode,
    adminStatus: data.adminStatus || 'active'
  }
  
  apiService.setToken(data.token, data.token)
  
  localStorage.setItem('userInfo', JSON.stringify(userInfo))
  localStorage.setItem('isLoggedIn', 'true')
  
  if (loginForm.remember) {
    localStorage.setItem('rememberedUsername', loginForm.username)
  } else {
    localStorage.removeItem('rememberedUsername')
  }
  
  ElMessage.success('登录成功')
  router.push('/')
}

const onVerificationSuccess = () => {
  if (pendingLoginCallback) {
    pendingLoginCallback()
    pendingLoginCallback = null
  }
}

const onVerificationFail = () => {
  ElMessage.error('验证失败，请重试')
  pendingLoginCallback = null
}

const initForm = () => {
  const rememberedUsername = localStorage.getItem('rememberedUsername')
  if (rememberedUsername) {
    loginForm.username = rememberedUsername
    loginForm.remember = true
  }
}

onMounted(() => {
  initForm()
})

const handleForgotPassword = () => {
  router.push('/forgot-password')
}
</script>

<style>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.login-form-wrapper {
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 420px;
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-header .logo {
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  border-radius: 50%;
  overflow: hidden;
}

.login-header h1 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.login-header p {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.login-form {
  width: 100%;
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.forgot-password {
  font-size: 14px;
}

.login-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 500;
}



/* 备案信息样式 */
.beian-info {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  padding: 1.5vw 0;
  font-size: 2.5vw;
  color: #333;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5vw;
}

.beian-info a {
  color: #333;
  text-decoration: none;
  transition: color 0.3s ease;
}

.beian-info a:hover {
  color: #4CAF50;
  text-decoration: underline;
}

/* 响应式字体大小调整 */
@media (min-width: 768px) {
  .beian-info {
    font-size: 12px;
    padding: 12px 0;
    gap: 4px;
  }
}

@media (max-width: 480px) {
  .beian-info {
    font-size: 10px;
    padding: 10px 0;
    gap: 3px;
  }
}
</style>
